import { ConversationAttachmentDTO, ConversationSummaryDTO } from 'models/conversations'
import { formatBytes } from 'utils/formatting/formatBytes'

type TranslateFn = (key: string, options?: Record<string, unknown>) => string

type LastMessagePreviewFields = Pick<ConversationSummaryDTO, 'lastMessagePreview' | 'lastMessageAttachmentCount' | 'lastMessageAttachmentFilename'>

export const MAX_ATTACHMENTS_PER_MESSAGE = 10

export const MAX_ATTACHMENT_SIZE_BYTES = 26_214_400

export const BLOCKED_ATTACHMENT_EXTENSIONS: ReadonlySet<string> = new Set([
  'exe', 'dmg', 'msi', 'bat', 'cmd', 'com', 'scr', 'pif',
  'app', 'vbs', 'js', 'wsh', 'wsf', 'ps1', 'sh', 'bash',
  'dll', 'sys', 'drv', 'bin', 'deb', 'rpm',
  'html', 'htm', 'xhtml', 'xht', 'svg', 'svgz', 'mht', 'mhtml',
  'hta', 'lnk', 'iso', 'img', 'vhd', 'vhdx', 'jar', 'jse',
  'vbe', 'wsc', 'cpl', 'reg', 'msp', 'msix', 'msixbundle', 'appx',
  'appxbundle', 'scf', 'url', 'xml', 'xsl',
])

const THUMBNAIL_CONTENT_TYPES: ReadonlySet<string> = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp'])

const BLOCKED_CONTENT_TYPES: ReadonlySet<string> = new Set(['text/xml', 'application/xml', 'text/html', 'application/xhtml+xml', 'image/svg+xml'])

const FORMAT_CHARACTERS = /\p{Cf}/gu

export enum AttachmentRejectionReason {
  TOO_MANY = 'TOO_MANY',
  TOO_LARGE = 'TOO_LARGE',
  BLOCKED_TYPE = 'BLOCKED_TYPE',
  EMPTY = 'EMPTY',
}

export interface AttachmentRejection {
  reason: AttachmentRejectionReason
  filename?: string
}

export interface AttachmentValidationResult {
  accepted: File[]
  rejections: AttachmentRejection[]
}

export const fileExtension = (filename: string): string => {
  const name = filename.replace(/[. ]+$/, '')
  const dot = name.lastIndexOf('.')
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase()
}

export const displayFilename = (filename: string): string => filename.replace(FORMAT_CHARACTERS, '')

export const attachmentContentType = (file: File): string => file.type || 'application/octet-stream'

export const isImageContentType = (contentType: string): boolean => contentType.toLowerCase().startsWith('image/')

const mediaTypeOf = (contentType: string): string => contentType.split(';')[0].trim().toLowerCase()

export const isThumbnailContentType = (contentType: string): boolean => THUMBNAIL_CONTENT_TYPES.has(mediaTypeOf(contentType))

export const isBlockedAttachment = (file: File): boolean => {
  const mediaType = mediaTypeOf(attachmentContentType(file))
  return BLOCKED_ATTACHMENT_EXTENSIONS.has(fileExtension(displayFilename(file.name))) ||
    BLOCKED_CONTENT_TYPES.has(mediaType) ||
    mediaType.endsWith('+xml')
}

export const validateAttachments = (files: File[], alreadyAttached: number): AttachmentValidationResult => {
  const accepted: File[] = []
  const rejections: AttachmentRejection[] = []
  let overflow = false

  for (const file of files) {
    if (isBlockedAttachment(file)) {
      rejections.push({ reason: AttachmentRejectionReason.BLOCKED_TYPE, filename: file.name })
    } else if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      rejections.push({ reason: AttachmentRejectionReason.TOO_LARGE, filename: file.name })
    } else if (file.size === 0) {
      rejections.push({ reason: AttachmentRejectionReason.EMPTY, filename: file.name })
    } else if (alreadyAttached + accepted.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
      overflow = true
    } else {
      accepted.push(file)
    }
  }

  if (overflow) rejections.push({ reason: AttachmentRejectionReason.TOO_MANY })

  return { accepted, rejections }
}

const REJECTION_KEYS: Record<AttachmentRejectionReason, string> = {
  [AttachmentRejectionReason.TOO_MANY]: 'chat:attachments.errors.too_many',
  [AttachmentRejectionReason.TOO_LARGE]: 'chat:attachments.errors.too_large',
  [AttachmentRejectionReason.BLOCKED_TYPE]: 'chat:attachments.errors.blocked_type',
  [AttachmentRejectionReason.EMPTY]: 'chat:attachments.errors.empty',
}

export const rejectionMessage = (rejection: AttachmentRejection): { key: string, values: Record<string, string | number> } => ({
  key: REJECTION_KEYS[rejection.reason],
  values: {
    filename: displayFilename(rejection.filename ?? ''),
    max: MAX_ATTACHMENTS_PER_MESSAGE,
    maxSize: formatBytes(MAX_ATTACHMENT_SIZE_BYTES, 0),
  },
})

export const lastMessageAttachmentFields = (
  attachments?: Pick<ConversationAttachmentDTO, 'filename'>[] | null
): Required<Pick<ConversationSummaryDTO, 'lastMessageAttachmentCount' | 'lastMessageAttachmentFilename'>> => ({
  lastMessageAttachmentCount: attachments?.length ?? 0,
  lastMessageAttachmentFilename: attachments?.[0]?.filename ?? null,
})

export const lastMessagePreviewText = (summary: LastMessagePreviewFields, t: TranslateFn): string => {
  const preview = summary.lastMessagePreview ?? ''
  const count = summary.lastMessageAttachmentCount ?? 0
  const filename = displayFilename(summary.lastMessageAttachmentFilename ?? '')
  if (preview.trim() || count < 1 || !filename) return preview
  return count > 1
    ? t('chat:attachments.preview_more', { filename, count: count - 1 })
    : t('chat:attachments.preview', { filename })
}
